import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  User, BookOpen, Award, Settings, LogOut, 
  Camera, Mail, Phone, MapPin, Calendar, CheckCircle 
} from 'lucide-react';

// --- MOCK DATA (Backend se connect hone tak) ---
const mockEnrolledCourses = [
  {
    id: 1,
    title: "Java Full Stack Development",
    instructor: "Rahul Sharma",
    progress: 45, // Percentage
    status: "In Progress",
    nextLesson: "Spring Boot Introduction"
  },
  {
    id: 2,
    title: "React JS & Tailwind CSS",
    instructor: "Priya Verma",
    progress: 100,
    status: "Completed",
    certificateLink: "#"
  }
];

const mockCertificates = [
  {
    id: 101,
    course: "React JS & Tailwind CSS",
    date: "15 Dec 2025",
    idNumber: "PIT-CERT-2025-001"
  }
];

const UserProfile: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'courses' | 'certificates' | 'settings'>('overview');

  // Local state for profile editing
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "+91 98765 43210", // Mock phone
    location: "Noida, India"
  });

  if (!user) {
    return <div className="p-10 text-center">Please log in to view your profile.</div>;
  }

  // --- SUB-COMPONENTS FOR TABS ---

  // 1. Overview Tab
  const OverviewSection = () => (
    <div className="space-y-6 animate-fade-in-up">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-center space-x-4">
          <div className="p-3 bg-blue-100 text-blue-600 rounded-full"><BookOpen size={24}/></div>
          <div>
            <p className="text-gray-500 text-sm">Enrolled Courses</p>
            <h3 className="text-2xl font-bold text-gray-800">{mockEnrolledCourses.length}</h3>
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-100 flex items-center space-x-4">
          <div className="p-3 bg-green-100 text-green-600 rounded-full"><Award size={24}/></div>
          <div>
            <p className="text-gray-500 text-sm">Certificates Earned</p>
            <h3 className="text-2xl font-bold text-gray-800">{mockCertificates.length}</h3>
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 flex items-center space-x-4">
          <div className="p-3 bg-purple-100 text-purple-600 rounded-full"><Calendar size={24}/></div>
          <div>
            <p className="text-gray-500 text-sm">Joined Since</p>
            <h3 className="text-xl font-bold text-gray-800">Jan 2025</h3>
          </div>
        </div>
      </div>

      {/* Current Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Continue Learning</h3>
        {mockEnrolledCourses.filter(c => c.status === "In Progress").map(course => (
          <div key={course.id} className="mb-4 last:mb-0">
            <div className="flex justify-between items-end mb-1">
              <span className="font-semibold text-gray-700">{course.title}</span>
              <span className="text-sm text-gray-500">{course.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-red-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${course.progress}%` }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Up Next: {course.nextLesson}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // 2. My Courses Tab
  const CoursesSection = () => (
    <div className="space-y-4 animate-fade-in-up">
      <h3 className="text-xl font-bold text-gray-800 mb-2">My Courses</h3>
      {mockEnrolledCourses.map(course => (
        <div key={course.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
           <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
                <BookOpen size={20} />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">{course.title}</h4>
                <p className="text-sm text-gray-500">Instructor: {course.instructor}</p>
              </div>
           </div>
           
           <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="flex flex-col items-end min-w-[100px]">
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${course.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {course.status}
                </span>
              </div>
              <button className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition">
                {course.status === 'Completed' ? 'View Details' : 'Continue'}
              </button>
           </div>
        </div>
      ))}
    </div>
  );

  // 3. Settings Tab
  const SettingsSection = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in-up">
      <h3 className="text-xl font-bold text-gray-800 mb-6">Profile Settings</h3>
      <form className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={18}/>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"/>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={18}/>
              <input type="email" value={formData.email} readOnly className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"/>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 text-gray-400" size={18}/>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"/>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 text-gray-400" size={18}/>
              <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"/>
            </div>
          </div>
        </div>
        
        <div className="pt-4 flex justify-end">
          <button type="button" className="px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 shadow-md transition">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6 flex flex-col md:flex-row items-center gap-6 relative overflow-hidden">
           {/* Decorative Background */}
           <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-red-700 to-red-900 opacity-90 z-0"></div>
           
           {/* Profile Image */}
           <div className="relative z-10 mt-8 md:mt-0">
              <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-500">
                {/* Mock Image Logic */}
                {user.name.charAt(0).toUpperCase()}
              </div>
              <button className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full shadow-md border border-gray-200 hover:text-red-600 text-gray-600">
                <Camera size={16} />
              </button>
           </div>

           {/* User Info */}
           <div className="flex-1 text-center md:text-left z-10 md:mt-8">
              <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
              <p className="text-gray-500">{user.email}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                 <span className="px-3 py-1 bg-red-50 text-red-700 text-xs font-semibold rounded-full border border-red-100">Student</span>
                 <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100">Web Development</span>
              </div>
           </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          
          {/* Sidebar Navigation */}
          <aside className="w-full md:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-24">
               <nav className="flex flex-col p-2 space-y-1">
                 <button 
                    onClick={() => setActiveTab('overview')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === 'overview' ? 'bg-red-50 text-red-700' : 'text-gray-600 hover:bg-gray-50'}`}
                 >
                    <User size={18} />
                    <span>Overview</span>
                 </button>
                 <button 
                    onClick={() => setActiveTab('courses')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === 'courses' ? 'bg-red-50 text-red-700' : 'text-gray-600 hover:bg-gray-50'}`}
                 >
                    <BookOpen size={18} />
                    <span>My Courses</span>
                 </button>
                 <button 
                    onClick={() => setActiveTab('certificates')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === 'certificates' ? 'bg-red-50 text-red-700' : 'text-gray-600 hover:bg-gray-50'}`}
                 >
                    <Award size={18} />
                    <span>Certificates</span>
                 </button>
                 <button 
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === 'settings' ? 'bg-red-50 text-red-700' : 'text-gray-600 hover:bg-gray-50'}`}
                 >
                    <Settings size={18} />
                    <span>Settings</span>
                 </button>
                 
                 <div className="border-t border-gray-100 my-2 pt-2">
                   <button 
                      onClick={logout}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left"
                   >
                      <LogOut size={18} />
                      <span>Log Out</span>
                   </button>
                 </div>
               </nav>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1">
             {activeTab === 'overview' && <OverviewSection />}
             {activeTab === 'courses' && <CoursesSection />}
             {activeTab === 'settings' && <SettingsSection />}
             
             {activeTab === 'certificates' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in-up">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">My Certificates</h3>
                    {mockCertificates.length > 0 ? (
                        mockCertificates.map(cert => (
                            <div key={cert.id} className="flex items-center justify-between p-4 border border-green-100 bg-green-50/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 text-green-700 rounded-full">
                                        <CheckCircle size={20} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-800">{cert.course}</p>
                                        <p className="text-xs text-gray-500">Issued: {cert.date} | ID: {cert.idNumber}</p>
                                    </div>
                                </div>
                                <button className="text-sm text-green-700 font-semibold hover:underline">Download</button>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500 text-sm">No certificates earned yet.</p>
                    )}
                </div>
             )}
          </main>

        </div>
      </div>
    </div>
  );
};

export default UserProfile;